import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  CreateOptimizationPayload,
  PreviewPayload,
  UpdateOptimizationPayload,
  createOptimization,
  getOptimization,
  getOptimizationOverview,
  listExternalProviders,
  listOptimizations,
  previewOptimization,
  sendOptimization,
  updateOptimization,
} from "../services/costOptimizer";

import { useSessionUser } from "./useAuth";

const optimizationsKey = (workspaceId: string) => [
  "costOptimizations",
  workspaceId,
];

const overviewKey = (workspaceId: string) => [
  "costOptimizationOverview",
  workspaceId,
];

export const useOptimizations = () => {
  const { data: sessionUser } = useSessionUser();
  return useQuery({
    enabled: !!sessionUser?.currentWorkspaceId,
    queryFn: listOptimizations,
    queryKey: optimizationsKey(sessionUser?.currentWorkspaceId || ""),
    refetchOnWindowFocus: false,
  });
};

export const useOptimization = (id: string | null) => {
  const { data: sessionUser } = useSessionUser();
  return useQuery({
    enabled: !!id && !!sessionUser?.currentWorkspaceId,
    queryFn: () => getOptimization(id as string),
    queryKey: [
      "costOptimization",
      sessionUser?.currentWorkspaceId || "",
      id || "",
    ],
    refetchOnWindowFocus: false,
  });
};

export const useOptimizationOverview = () => {
  const { data: sessionUser } = useSessionUser();
  return useQuery({
    enabled: !!sessionUser?.currentWorkspaceId,
    queryFn: getOptimizationOverview,
    queryKey: overviewKey(sessionUser?.currentWorkspaceId || ""),
    refetchOnWindowFocus: false,
  });
};

export const usePreviewOptimization = () =>
  useMutation({
    mutationFn: (payload: PreviewPayload) => previewOptimization(payload),
  });

export const useCreateOptimization = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateOptimizationPayload) =>
      createOptimization(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["costOptimizations"] });
      queryClient.invalidateQueries({ queryKey: ["costOptimizationOverview"] });
    },
  });
};

export const useUpdateOptimization = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: UpdateOptimizationPayload;
    }) => updateOptimization(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["costOptimizations"] });
    },
  });
};

export const useSendOptimization = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => sendOptimization(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["costOptimizations"] });
      queryClient.invalidateQueries({ queryKey: ["costOptimizationOverview"] });
    },
  });
};

export const useExternalProviders = () => {
  const { data: sessionUser } = useSessionUser();
  return useQuery({
    enabled: !!sessionUser?.currentWorkspaceId,
    queryFn: listExternalProviders,
    queryKey: [
      "costOptimizerExternalProviders",
      sessionUser?.currentWorkspaceId,
    ],
    refetchOnWindowFocus: false,
  });
};
